import { useState, useMemo, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Phone, Mail, User, Loader2, Calendar as CalendarIcon, Clock, CheckCircle2, X,
} from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";

const API = process.env.REACT_APP_BACKEND_URL;

// Generate 30-min slots from 09:00 to 17:00
const TIME_SLOTS = (() => {
  const slots = [];
  for (let h = 9; h <= 16; h++) {
    slots.push({ v: `${String(h).padStart(2, "0")}:00`, label: formatHour(h, 0) });
    slots.push({ v: `${String(h).padStart(2, "0")}:30`, label: formatHour(h, 30) });
  }
  slots.push({ v: "17:00", label: "5:00 PM" });
  return slots;
})();

function formatHour(h, m) {
  const period = h >= 12 ? "PM" : "AM";
  const hh = h % 12 === 0 ? 12 : h % 12;
  return `${hh}:${String(m).padStart(2, "0")} ${period}`;
}

function toISODate(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

/**
 * Broker call-back scheduling modal.
 * Opens after the user unlocks a Premium Buying Power report and taps
 * "Talk to a Broker". Captures: full name, email, phone, preferred date
 * (from tomorrow onward, incl. weekends), and 30-min slot 9 AM – 5 PM.
 */
export default function BrokerScheduleModal({ open, onClose, defaults = {} }) {
  const [name, setName] = useState(defaults.name || "");
  const [email, setEmail] = useState(defaults.email || "");
  const [phone, setPhone] = useState(defaults.phone || "");
  const [date, setDate] = useState("");
  const [time, setTime] = useState("");
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null); // {date, time}

  // Hydrate defaults when modal opens
  useEffect(() => {
    if (open) {
      setName(defaults.name || "");
      setEmail(defaults.email || "");
      setPhone(defaults.phone || "");
      setDate("");
      setTime("");
      setNotes("");
      setDone(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const { minDate, maxDate } = useMemo(() => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    const far = new Date();
    far.setMonth(far.getMonth() + 2);
    return { minDate: toISODate(tomorrow), maxDate: toISODate(far) };
  }, [open]);

  if (!open) return null;

  const submit = async () => {
    if (!name.trim()) return toast.error("Please enter your name.");
    if (!email.trim()) return toast.error("Please enter your email.");
    if (!phone.trim()) return toast.error("Please enter your phone number.");
    if (!date) return toast.error("Please pick a date.");
    if (!time) return toast.error("Please pick a time slot.");
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/api/broker/schedule`, {
        name: name.trim(),
        email: email.trim(),
        phone: phone.trim(),
        scheduled_date: date,
        scheduled_time: time,
        notes: notes.trim() || null,
        listing_url: defaults.listing_url || null,
        listing_price: defaults.listing_price || null,
        source: defaults.source || "buying_power",
      });
      setDone({ date: res.data.scheduled_date, time: res.data.scheduled_time });
      toast.success("A broker will call you back at the time you picked.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not schedule your call-back. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const timeLabel = TIME_SLOTS.find((s) => s.v === time)?.label || time;

  return (
    <div
      className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      data-testid="broker-schedule-modal"
    >
      <div className="bg-white rounded-3xl shadow-2xl w-full max-w-md max-h-[90vh] overflow-y-auto">
        <div className="relative p-6 sm:p-8">
          <button
            type="button"
            onClick={onClose}
            className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition"
            aria-label="Close"
            data-testid="broker-modal-close"
          >
            <X className="w-5 h-5" />
          </button>

          {done ? (
            <div className="text-center py-6" data-testid="broker-schedule-success">
              <div className="w-14 h-14 mx-auto rounded-full bg-emerald-50 flex items-center justify-center mb-4">
                <CheckCircle2 className="w-7 h-7 text-emerald-500" />
              </div>
              <h2 className="text-xl font-bold text-[#2D3748] mb-2 font-cabinet">You're on the broker's calendar</h2>
              <p className="text-sm text-gray-500 mb-5">
                We'll call <strong>{phone}</strong> on <strong>{done.date}</strong> at{" "}
                <strong>{TIME_SLOTS.find((s) => s.v === done.time)?.label || done.time}</strong> (Nassau time).
              </p>
              <Button className="btn-primary w-full h-11" onClick={onClose} data-testid="broker-schedule-done">
                Great, thanks!
              </Button>
            </div>
          ) : (
            <>
              <div className="text-center mb-5">
                <div className="w-12 h-12 mx-auto rounded-2xl bg-gradient-to-br from-[#52BFB8] to-[#3E9690] flex items-center justify-center mb-3">
                  <Phone className="w-6 h-6 text-white" />
                </div>
                <h2 className="text-2xl font-bold text-[#2D3748] mb-1 font-cabinet">Book a broker call-back</h2>
                <p className="text-sm text-gray-500">Pick a time and we'll call you. No wait, no voicemail.</p>
              </div>

              <div className="space-y-3">
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input
                    type="text"
                    placeholder="Full name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className="pl-10 h-11"
                    data-testid="broker-name-input"
                  />
                </div>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input
                    type="email"
                    placeholder="Email address"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="pl-10 h-11"
                    data-testid="broker-email-input"
                  />
                </div>
                <div className="relative">
                  <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input
                    type="tel"
                    placeholder="Best call-back number"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    className="pl-10 h-11"
                    data-testid="broker-phone-input"
                  />
                </div>

                <div>
                  <label className="text-xs font-medium text-gray-500 flex items-center gap-1 mb-1">
                    <CalendarIcon className="w-3.5 h-3.5" /> Preferred date
                  </label>
                  <Input
                    type="date"
                    min={minDate}
                    max={maxDate}
                    value={date}
                    onChange={(e) => setDate(e.target.value)}
                    className="h-11"
                    data-testid="broker-date-input"
                  />
                  <p className="text-[11px] text-gray-400 mt-1">
                    Available from tomorrow — weekends included.
                  </p>
                </div>

                <div>
                  <label className="text-xs font-medium text-gray-500 flex items-center gap-1 mb-1">
                    <Clock className="w-3.5 h-3.5" /> Preferred time (Nassau)
                  </label>
                  <div className="grid grid-cols-4 gap-2 max-h-48 overflow-y-auto pr-1" data-testid="broker-time-grid">
                    {TIME_SLOTS.map((s) => (
                      <button
                        key={s.v}
                        type="button"
                        onClick={() => setTime(s.v)}
                        className={`text-xs py-2 rounded-lg border transition ${
                          time === s.v
                            ? "border-[#52BFB8] bg-[#52BFB8]/10 text-[#2D3748] font-semibold"
                            : "border-gray-200 text-gray-600 hover:border-[#52BFB8]/40"
                        }`}
                        data-testid={`broker-time-${s.v}`}
                      >
                        {s.label}
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="text-xs font-medium text-gray-500 mb-1 block">
                    Anything we should know? (optional)
                  </label>
                  <textarea
                    rows={2}
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    placeholder="e.g. First-time buyer, flexible on neighborhood…"
                    className="w-full text-sm rounded-xl border border-gray-200 px-3 py-2 focus:outline-none focus:border-[#52BFB8] focus:ring-2 focus:ring-[#52BFB8]/20 resize-none"
                    data-testid="broker-notes-input"
                  />
                </div>

                {date && time && (
                  <div className="bg-[#52BFB8]/5 border border-[#52BFB8]/20 rounded-xl p-3 text-sm text-[#2D3748]" data-testid="broker-schedule-summary">
                    We'll call <strong>{phone || "you"}</strong> on <strong>{date}</strong> at <strong>{timeLabel}</strong>.
                  </div>
                )}

                <Button
                  onClick={submit}
                  disabled={submitting}
                  className="btn-primary w-full h-11 inline-flex items-center justify-center gap-2"
                  data-testid="broker-submit-btn"
                >
                  {submitting ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" /> Scheduling…
                    </>
                  ) : (
                    <>Confirm Call-back</>
                  )}
                </Button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
